import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Route, Routes, Navigate } from 'react-router-dom';
import propTypes from 'prop-types';
import { ThemeProvider, CssBaseline } from '@mui/material';

import {
  LoginPage,
  DummyPage,
  InvitePage,
  getToken,
  CreatePasswordPage,
  TwoFactorAuthPage,
  AccountSelectionPage,
  getSettings,
  isFullyAuthenticated,
} from './auth';
import { TerraPanelPage } from './panel';
import { OnboardingWizardPage } from './onboarding-wizard';
import { getUser, fetchLoggedInUser, impersonateAccount } from './user';
import { InnerLoader } from './ui';
import { fetchAccount, getAccount } from './account';
import muiTheme from './ui/theme/muiTheme';

const App = ({
  token,
  user,
  settings,
  account,
  fetchLoggedInUser,
  impersonateAccount,
  fetchAccount,
}) => {
  const [loading, setLoading] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Store token validity as a value, not a function call. A 2FA-pending
  // sign-in leaves the token slice VALID with no credentials — that must
  // not count as authenticated here, or App flips to the authed tree and
  // fetches /me with no usable token (401 → SIGN_OUT → login remount).
  const isTokenValid = isFullyAuthenticated(token);
  const originalConsoleError = console.error;
  console.error = (...args) => {
    if (
      typeof args[0] === 'string' &&
      args[0].includes('Support for defaultProps will be removed')
    ) {
      return;
    }
    originalConsoleError(...args);
  };
  useEffect(() => {
    async function load() {
      if (isTokenValid && !user.id && !loading) {
        setLoading(true);
        await fetchLoggedInUser();
        setLoading(false);
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTokenValid, user.id, loading]);

  useEffect(() => {
    async function setAccount() {
      await impersonateAccount({
        accountId: settings.selectedAccountId,
        accountName: settings.selectedAccountName,
        hasConsents: settings.selectedAccountHasConsents,
        hasDocuments: settings.selectedAccountHasDocuments,
      });

      await fetchAccount(settings.selectedAccountId);
    }

    if (!settingsLoaded && user.id) {
      if (
        (user.isBankAdmin || user.isBankReadOnly) &&
        account.id !== settings.selectedAccountId
      ) {
        setAccount();
      }
      setSettingsLoaded(true);
    }
    // Only depend on specific properties that are used, not entire objects
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user.id,
    user.isBankAdmin,
    user.isBankReadOnly,
    account.id,
    settings.selectedAccountId,
    settings.selectedAccountName,
    settings.selectedAccountHasConsents,
    settings.selectedAccountHasDocuments,
    settingsLoaded,
    impersonateAccount,
    fetchAccount,
  ]);

  if (!isTokenValid) {
    return (
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <main className="App">
          <Routes>
            <Route path="/" element={<LoginPage />} />
            <Route
              path="/create-password/:activationToken"
              element={<CreatePasswordPage />}
            />
            <Route
              path="/activate/:activationToken"
              element={<TwoFactorAuthPage />}
            />
            <Route path="/reset/:token" element={<CreatePasswordPage />} />
            <Route path="/two-factor-auth" element={<TwoFactorAuthPage />} />
            <Route path="/*" element={<Navigate to="/" />} />
          </Routes>
        </main>
      </ThemeProvider>
    );
  }

  if (!loading && user.id) {
    if (user.isBankAdmin || user.isSuperAdmin || user.isSolventAdmin || user.isBankReadOnly) {
      return (
        <ThemeProvider theme={muiTheme}>
          <CssBaseline />
          <main className="App">
            <Routes>
              <Route path="/dummy" element={<DummyPage />} />
              <Route path="/reset/:token" element={<CreatePasswordPage />} />
              <Route path="/account-selection" element={<Navigate to="/" />} />
              <Route path="/*" element={<TerraPanelPage />} />
            </Routes>
          </main>
        </ThemeProvider>
      );
    }

    if (
      !user.hasFinishedOnboarding &&
      user.isMerchantAdmin &&
      (user.financialInstitutionBgCheckNeeded ||
        user.financialInstitutionNdaNeeded)
    ) {
      return (
        <ThemeProvider theme={muiTheme}>
          <CssBaseline />
          <main className="App">
            <Routes>
              <Route path="/invite" element={<InvitePage />} />
              <Route path="/wizard/:step" element={<OnboardingWizardPage />} />
              <Route path="/wizard" element={<OnboardingWizardPage />} />
              <Route path="/reset/:token" element={<CreatePasswordPage />} />
              <Route
                path="/account-selection"
                element={<AccountSelectionPage />}
              />
              <Route path="/" element={<Navigate to="/wizard" />} />
            </Routes>
          </main>
        </ThemeProvider>
      );
    }

    if (!user.hasFinishedOnboarding && user.financialInstitutionBgCheckNeeded) {
      return (
        <ThemeProvider theme={muiTheme}>
          <CssBaseline />
          <main className="App">
            <Routes>
              <Route path="/invite" element={<InvitePage />} />
              <Route path="/wizard/:step" element={<OnboardingWizardPage />} />
              <Route path="/wizard" element={<OnboardingWizardPage />} />
              <Route path="/reset/:token" element={<CreatePasswordPage />} />
              <Route
                path="/account-selection"
                element={<AccountSelectionPage />}
              />
              <Route path="/" element={<Navigate to="/wizard" />} />
            </Routes>
          </main>
        </ThemeProvider>
      );
    }

    return (
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <main className="App">
          <Routes>
            <Route path="/dummy" element={<DummyPage />} />
            <Route path="/invite" element={<InvitePage />} />
            <Route path="/reset/:token" element={<CreatePasswordPage />} />
            <Route
              path="/account-selection"
              element={<AccountSelectionPage />}
            />
            <Route path="/*" element={<TerraPanelPage />} />
          </Routes>
        </main>
      </ThemeProvider>
    );
  }
  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <InnerLoader visible />
    </ThemeProvider>
  );
};

App.propTypes = {
  token: propTypes.object.isRequired,
  user: propTypes.object.isRequired,
  settings: propTypes.object.isRequired,
  account: propTypes.object.isRequired,
  fetchLoggedInUser: propTypes.func.isRequired,
  impersonateAccount: propTypes.func.isRequired,
  fetchAccount: propTypes.func.isRequired,
};

const mapStateToProps = state => ({
  token: getToken(state),
  user: getUser(state),
  settings: getSettings(state),
  account: getAccount(state),
});

// React Router v6 no longer has withRouter
// Instead we connect directly
export default connect(mapStateToProps, {
  fetchLoggedInUser,
  impersonateAccount,
  fetchAccount,
})(App);
